import { useState } from "react";
import { ChevronDown, ChevronUp, Copy, Loader2, Sparkles } from "lucide-react";
import { useUiLanguage } from "../../i18n/UiLanguageProvider";
import { mailV2Api } from "../../api/mail-v2";

const TONE_KEYS = ["promotion", "natural", "polite", "casual"] as const;

function getToneOptions(
  pick: (ja: string, en: string) => string,
): {
  key: typeof TONE_KEYS[number];
  label: string;
  promptHint: string;
}[] {
  return [
    {
      key: "promotion",
      label: pick("販促", "Promotional"),
      promptHint: pick("販路を促進する力強いトーン", "A strong promotional tone that drives action"),
    },
    {
      key: "natural",
      label: pick("自然", "Natural"),
      promptHint: pick("自然な柔らかいトーン", "A natural and soft tone"),
    },
    {
      key: "polite",
      label: pick("丁寧", "Polite"),
      promptHint: pick("丁寧で礼儀正しいトーン", "A polite and respectful tone"),
    },
    {
      key: "casual",
      label: pick("カジュアル", "Casual"),
      promptHint: pick("くだけたカジュアルなトーン", "A relaxed and casual tone"),
    },
  ];
}

function extractHashtags(text: string): string[] {
  const matches = text.match(/#[\wぁ-んァ-ヶ一-龯]+/g) ?? [];
  return matches.map((match) => match.replace(/^#+/, ""));
}

function stripHashtags(text: string): string {
  return text.replace(/[#][\wぁ-んァ-ヶ一-龯]+\s*/g, "").trim();
}

export function InstagramV2AiAssistant({
  managementTitle,
  caption,
  selectedMediaName,
  onApplyCaption,
  onApplyHashtags,
  collapsible = false,
  defaultCollapsed = false,
}: {
  managementTitle: string;
  caption: string;
  selectedMediaName: string | null;
  onApplyCaption: (text: string) => void;
  onApplyHashtags: (tags: string[]) => void;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}) {
  const { pick } = useUiLanguage();
  const toneOptions = getToneOptions(pick);
  const [tone, setTone] = useState<typeof TONE_KEYS[number]>("natural");
  const [instruction, setInstruction] = useState("");
  const [generated, setGenerated] = useState<{
    text: string;
    caption: string;
    hashtags: string[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const handleGenerate = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const toneHint = toneOptions.find((item) => item.key === tone)?.promptHint ?? "";
      const parts = [
        toneHint ? `${pick("トーン", "Tone")}: ${toneHint}` : null,
        managementTitle ? `${pick("タイトル", "Title")}: ${managementTitle}` : null,
        caption ? `${pick("既存キャプション", "Existing caption")}: ${caption}` : null,
        selectedMediaName ? `${pick("画像", "Image")}: ${selectedMediaName}` : null,
        instruction ? `${pick("追加指示", "Additional instructions")}: ${instruction}` : null,
      ].filter(Boolean);
      const composedInstruction = parts.length > 0
        ? parts.join("\n")
        : pick("Instagram 投稿用のキャプションとハッシュタグを生成してください。", "Generate an Instagram caption and hashtags.");

      const result = await mailV2Api.generateAiCompose({
        instruction: composedInstruction,
        tone: "custom",
      });

      const text = result.text ?? "";
      setGenerated({
        text,
        caption: stripHashtags(text),
        hashtags: extractHashtags(text),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : pick("AI 生成に失敗しました", "AI generation failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated.text);
    } catch {
      // noop
    }
  };

  return (
    <div className={`igv2-compose-assistant ${collapsed ? "collapsed" : ""}`}>
      <div className="igv2-compose-assistant-head">
        <div className="igv2-compose-assistant-title">
          <Sparkles size={14} />
          <strong>{pick("AI投稿アシスタント", "AI Post Assistant")}</strong>
        </div>
        {collapsible ? (
          <button
            type="button"
            className="igv2-compose-assistant-toggle"
            onClick={() => setCollapsed((prev) => !prev)}
            aria-expanded={!collapsed}
          >
            {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            {collapsed ? pick("開く", "Expand") : pick("折りたたむ", "Collapse")}
          </button>
        ) : null}
      </div>
      <p>{pick("キャプションとハッシュタグ候補をすばやく生成します。", "Quickly generate caption and hashtag ideas.")}</p>

      {!collapsed ? (
        <>
          <label className="igv2-compose-label">{pick("投稿トーン", "Tone")}</label>
          <div className="igv2-compose-tone-group">
            {toneOptions.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`igv2-compose-tone-pill ${tone === item.key ? "active" : ""}`}
                onClick={() => setTone(item.key)}
                disabled={loading}
              >
                {item.label}
              </button>
            ))}
          </div>

          <label className="igv2-compose-label">{pick("AIへの指示", "Instructions for AI")}</label>
          <textarea
            className="igv2-compose-textarea igv2-compose-textarea--compact"
            placeholder={pick("例: 新商品のアピールポイントを 3 つ入れる", "e.g. include three product selling points")}
            value={instruction}
            onChange={(event) => setInstruction(event.target.value)}
            disabled={loading}
          />

          <button
            type="button"
            className="igv2-compose-gradient-button"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? <Loader2 size={14} className="igv2-spin" /> : <Sparkles size={14} />}
            {pick("キャプション・ハッシュタグを生成", "Generate caption and hashtags")}
          </button>

          {error ? <div className="igv2-compose-error">{error}</div> : null}

          {generated ? (
            <div className="igv2-compose-ai-result">
              <div className="igv2-row-between">
                <strong>{pick("生成結果", "Generated result")}</strong>
                <button type="button" className="igv2-compose-ghost-button" onClick={handleCopy}>
                  <Copy size={12} />
                  {pick("コピー", "Copy")}
                </button>
              </div>
              <div className="igv2-compose-ai-caption">{generated.caption}</div>
              {generated.hashtags.length > 0 ? (
                <div className="igv2-compose-hashtag-list">
                  {generated.hashtags.map((tag) => (
                    <span key={tag} className="igv2-compose-hashtag-chip">#{tag}</span>
                  ))}
                </div>
              ) : null}
              <div className="igv2-compose-ai-actions">
                <button type="button" className="igv2-compose-ghost-button" onClick={() => onApplyCaption(generated.caption)}>
                  {pick("本文へ反映", "Apply caption")}
                </button>
                {generated.hashtags.length > 0 ? (
                  <button type="button" className="igv2-compose-ghost-button" onClick={() => onApplyHashtags(generated.hashtags)}>
                    {pick("ハッシュタグを反映", "Apply hashtags")}
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
